export interface TimeZoneOption {
  id: string;
  label: string;
}

const FALLBACK_TIME_ZONE_IDS = [
  'UTC',
  'Etc/GMT+12',
  'Etc/GMT+11',
  'Etc/GMT+10',
  'Etc/GMT+9',
  'Etc/GMT+8',
  'Etc/GMT+7',
  'Etc/GMT+6',
  'Etc/GMT+5',
  'Etc/GMT+4',
  'Etc/GMT+3',
  'Etc/GMT+2',
  'Etc/GMT+1',
  'Etc/GMT-1',
  'Etc/GMT-2',
  'Etc/GMT-3',
  'Etc/GMT-4',
  'Etc/GMT-5',
  'Etc/GMT-6',
  'Etc/GMT-7',
  'Etc/GMT-8',
  'Etc/GMT-9',
  'Etc/GMT-10',
  'Etc/GMT-11',
  'Etc/GMT-12',
  'Etc/GMT-13',
  'Etc/GMT-14',
  'Pacific/Midway',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Sao_Paulo',
  'Atlantic/Azores',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Moscow',
  'Africa/Cairo',
  'Asia/Dubai',
  'Asia/Baku',
  'Asia/Tashkent',
  'Asia/Yekaterinburg',
  'Asia/Kolkata',
  'Asia/Kathmandu',
  'Asia/Dhaka',
  'Asia/Almaty',
  'Asia/Bangkok',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
] as const;

export function defaultOrchestrationTimeZoneId(): string {
  const ids = listTimeZoneIds();
  return ids.includes('Asia/Tashkent') ? 'Asia/Tashkent' : 'UTC';
}

export function listTimeZoneIds(extraIds: readonly string[] = []): string[] {
  const ids = new Set<string>(FALLBACK_TIME_ZONE_IDS);
  if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl) {
    for (const id of Intl.supportedValuesOf('timeZone')) {
      ids.add(id);
    }
  }
  for (const id of extraIds) {
    const trimmed = id.trim();
    if (trimmed) {
      ids.add(trimmed);
    }
  }
  return [...ids];
}

export function formatUtcOffset(timeZoneId: string, at = new Date()): string {
  try {
    const name = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneId,
      timeZoneName: 'longOffset',
    })
      .formatToParts(at)
      .find(part => part.type === 'timeZoneName')?.value ?? 'UTC';
    return normalizeUtcOffset(name);
  } catch {
    return 'UTC';
  }
}

export function formatTimeZoneLabel(timeZoneId: string, at = new Date()): string {
  const offset = formatUtcOffset(timeZoneId, at);
  const name = timeZoneId.trim() || 'UTC';
  if (name === 'UTC' || name === 'Etc/UTC' || name === 'Etc/GMT') {
    return offset;
  }
  return `${offset} · ${name.replace(/_/g, ' ')}`;
}

export function buildTimeZoneOptions(at = new Date(), extraIds: readonly string[] = []): TimeZoneOption[] {
  return listTimeZoneIds(extraIds)
    .map(id => ({
      id,
      label: formatTimeZoneLabel(id, at),
      offsetMinutes: utcOffsetMinutes(id, at),
    }))
    .sort((left, right) => left.offsetMinutes - right.offsetMinutes || left.id.localeCompare(right.id))
    .map(({ id, label }) => ({ id, label }));
}

function normalizeUtcOffset(name: string): string {
  if (name === 'GMT' || name === 'UTC') {
    return 'UTC+00:00';
  }
  const match = name.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?$/);
  if (!match) {
    return name.replace(/^GMT/, 'UTC');
  }
  return `UTC${match[1]}${match[2].padStart(2, '0')}:${(match[3] ?? '00').padStart(2, '0')}`;
}

function utcOffsetMinutes(timeZoneId: string, at: Date): number {
  const offset = formatUtcOffset(timeZoneId, at);
  const match = offset.match(/^UTC([+-])(\d{2}):(\d{2})$/);
  if (!match) {
    return 0;
  }
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}
